/**
 * Кремль: a fortress in a block.
 *
 * Written as code rather than as pictures - see PlannedBuilding in
 * blueprint.ts for why. Sixty-one blocks on a side, thirty-eight high, and
 * about twenty thousand blocks laid in one go.
 *
 * **What it is made of, and what that costs.** Red брick for the curtain and
 * the towers, білий камінь for the cathedrals, зелена черепиця for the roofs,
 * позолочена бляха for the domes. Every one of those is masonry, and masonry
 * does not burn - `burnRate` says so and will keep saying so. Only the floors,
 * the roof timbers and the смолоскипи on the wall will take a flame. Bringing
 * this down needs fire that carries its own fuel, which is a weapon and not a
 * property of a wall.
 *
 * Pure: local coordinates and a callback. It has never heard of a world, a
 * chunk or a renderer, and it can be laid out and counted in a test.
 */

import { BlockId, type DoorFacing } from "../block/registry.js";
import type { PlannedBuilding } from "./blueprint.js";

/** Across, and back to front. Odd, so the gate has a middle to stand in. */
export const KREMLIN_SPAN = 61;

/**
 * How far below the hilltop the fortress is founded.
 *
 * Not a style choice: the island is a steep dome, and sitting the fortress on
 * the ground at its middle put it on a pillar of its own foundations with the
 * sea lapping six blocks outside the wall. Set into the hill it reads as
 * something built on the island rather than dropped onto it.
 */
export const KREMLIN_DROP = 15;
/**
 * How far in front of the gate the way up to it is cut.
 *
 * The кремль is founded fifteen blocks into the island's dome and its gate
 * is thirty blocks from the middle of that dome - so where the gate comes
 * out depends on what the hillside is doing thirty blocks away, and across a
 * dozen seeds that ranged from the sill sitting two blocks *under* the
 * ground to standing thirteen blocks over it. Both are the same bug: "кремль
 * занадто низько бо вхід опинився під землею".
 *
 * Sixteen blocks of approach, climbing or descending at most a block a step,
 * is the same answer the замок's gate already had - see `openApproach` - and
 * for the same reason: whatever the country does in front of the gate, there
 * has to be a road.
 */
export const KREMLIN_APPROACH = 16;

/** The gate's passage, in the blueprint's own local coordinates. */
export const KREMLIN_GATE = {
  /** Across the front wall, centred on the anchor. */
  fromX: 28, toX: 32,
  /** The opening above the floor: the floor itself is local y nought. */
  fromY: 1, toY: 5,
} as const;

/**
 * How wide the flight outside the gate is, measured from its middle.
 *
 * Two: five blocks across, which is the width of the gate's own passage. The
 * approach used to be three wide and made of earth, and a way in narrower
 * than the door it leads to looks like a path somebody wore rather than a
 * thing the fortress was built with.
 */
export const KREMLIN_STAIR_HALF = 2;

/**
 * How deep a step's pier may go looking for ground.
 *
 * Only reached where the steps walk out over the plateau's edge - the west
 * face of the кремль is a cliff into the sea on most seeds. Deep enough for
 * that drop, and bounded so a flight over deep water builds a pier rather
 * than a mole.
 */
export const KREMLIN_STAIR_PIER = 24;

/** Enough for the Спаська spire and the star on top of it. */
export const KREMLIN_HEIGHT = 38;

/** The band the curtain wall occupies, measured in from each edge. */
const WALL_THICK = 3;
/** Top course of the wall proper. The walkway is the surface above it. */
const WALL_TOP = 8;

type Put = (x: number, y: number, z: number, id: number) => void;

/** A solid run of blocks, inclusive at both ends. */
function box(
  put: Put, x0: number, x1: number, y0: number, y1: number,
  z0: number, z1: number, id: number,
): void {
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) put(x, y, z, id);
    }
  }
}

/** The four walls of a box, leaving the inside alone. */
function shell(
  put: Put, x0: number, x1: number, y0: number, y1: number,
  z0: number, z1: number, id: number,
): void {
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        if (x !== x0 && x !== x1 && z !== z0 && z !== z1) continue;
        put(x, y, z, id);
      }
    }
  }
}

/**
 * A flight of steps up the inside of the curtain, one block per course.
 *
 * Without these the walkway is decoration: this game has no ladder and no
 * stair block, so a wall you cannot get onto is a wall you can only look at.
 * A stepped ramp is what a voxel game had before stairs existed, and it is
 * what the towers' own floors are reached by too.
 */
function steps(put: Put, x: number, z0: number, dz: number): void {
  for (let step = 1; step <= WALL_TOP; step++) {
    const z = z0 + dz * step;
    // Solid underneath, so it is a stair and not a floating staircase.
    box(put, x, x, 1, step, z, z, BlockId.Brick);
  }
}

/**
 * The curtain wall, with a walkway behind its merlons.
 *
 * The merlons are the ластівчин хвіст the Kremlin is known for: three blocks
 * of merlon, a notch cut out of the middle of the top course, then a two-block
 * gap. At sixteen pixels a block that swallowtail is three blocks wide or it
 * is not readable at all.
 */
function curtain(put: Put): void {
  const last = KREMLIN_SPAN - 1;
  const inWall = (x: number, z: number): boolean =>
    x < WALL_THICK || z < WALL_THICK || x > last - WALL_THICK || z > last - WALL_THICK;
  const onOuter = (x: number, z: number): boolean =>
    x === 0 || z === 0 || x === last || z === last;

  for (let z = 0; z <= last; z++) {
    for (let x = 0; x <= last; x++) {
      if (!inWall(x, z)) continue;
      box(put, x, x, 1, WALL_TOP, z, z, BlockId.Brick);

      if (!onOuter(x, z)) continue;
      // Position along whichever edge this is, so the merlons of all four
      // walls step in time with each other instead of drifting at the corners.
      const along = (x === 0 || x === last) ? z : x;
      const phase = along % 5;
      if (phase >= 3) continue;
      put(x, WALL_TOP + 1, z, BlockId.Brick);
      // The notch: the middle block of each merlon stops one course lower.
      if (phase !== 1) put(x, WALL_TOP + 2, z, BlockId.Brick);
    }
  }

}

/**
 * A tower: a hollow brick shaft, a green tent roof, and something on top.
 *
 * @param half how far the tower reaches from its middle - so it is always odd
 * and always has a centre column for the spire to stand in.
 */
function tower(
  put: Put, cx: number, cz: number, half: number, height: number,
  spire: boolean,
): void {
  const x0 = cx - half, x1 = cx + half;
  const z0 = cz - half, z1 = cz + half;

  shell(put, x0, x1, 1, height, z0, z1, BlockId.Brick);
  // A floor every six courses. Boards, because a tower wants something in it
  // that a fire could take hold of.
  for (let y = 6; y < height; y += 6) {
    box(put, x0 + 1, x1 - 1, y, y, z0 + 1, z1 - 1, BlockId.PinePlanks);
  }
  // Loopholes, two courses tall, on all four faces.
  for (let y = height - 5; y <= height - 4; y++) {
    put(x0, y, cz, BlockId.Air);
    put(x1, y, cz, BlockId.Air);
    put(cx, y, z0, BlockId.Air);
    put(cx, y, z1, BlockId.Air);
  }

  /*
   * A door, on whichever face looks into the courtyard.
   *
   * Never on an outward face: a tower is part of the curtain, and a door in
   * its outer wall is a hole in the fortress. Which face that is comes from
   * where the tower sits relative to the middle, so it is right for a corner
   * tower and a gate tower without either being a special case.
   */
  const mid = (KREMLIN_SPAN - 1) / 2;
  if (Math.abs(cx - mid) >= Math.abs(cz - mid)) {
    const face = cx < mid ? x1 : x0;
    box(put, face, face, 1, 3, cz - 1, cz + 1, BlockId.Air);
  } else {
    const face = cz < mid ? z1 : z0;
    box(put, face, face, 1, 3, cx - 1, cx + 1, BlockId.Air);
  }

  // A machicolated course oversailing the shaft, the way every one of these
  // towers widens just under its roof.
  const brow = height + 1;
  shell(put, x0 - 1, x1 + 1, brow, brow, z0 - 1, z1 + 1, BlockId.Brick);

  // The tent roof, drawing in one ring a course until it closes.
  let ring = half + 1;
  let y = brow + 1;
  while (ring >= 0) {
    shell(put, cx - ring, cx + ring, y, y, cz - ring, cz + ring, BlockId.GreenTile);
    if (ring === 0) box(put, cx, cx, y, y, cz, cz, BlockId.GreenTile);
    ring--;
    y++;
  }

  if (!spire) return;
  // The spire, and the star on it. The star is the only thing in this
  // building that gives off light of its own.
  box(put, cx, cx, y, y + 2, cz, cz, BlockId.Gilt);
  put(cx, y + 3, cz, BlockId.RubyStar);
}

/**
 * An onion dome on a drum: white stone, gilding, and a cross.
 *
 * Five courses of gilt in a bulb that is widest in the middle - a straight
 * gilded cylinder reads as a chimney, and the swell is the entire difference
 * between a dome and a pipe.
 */
function dome(put: Put, cx: number, cz: number, y: number, big: boolean): void {
  const r = big ? 2 : 1;
  // The drum the dome sits on.
  shell(put, cx - r, cx + r, y, y + 2, cz - r, cz + r, BlockId.WhiteStone);
  let top = y + 3;
  box(put, cx - r, cx + r, top, top, cz - r, cz + r, BlockId.Gilt);
  top++;
  if (big) {
    box(put, cx - r, cx + r, top, top, cz - r, cz + r, BlockId.Gilt);
    top++;
  }
  // Drawing in: the shoulders come off first, then the sides.
  for (let x = cx - r + 1; x <= cx + r - 1; x++) {
    for (let z = cz - r + 1; z <= cz + r - 1; z++) put(x, top, z, BlockId.Gilt);
  }
  if (r === 1) {
    put(cx, top, cz, BlockId.Gilt);
  }
  top++;
  put(cx, top, cz, BlockId.Gilt);
  // The cross: a staff with one arm.
  put(cx, top + 1, cz, BlockId.Gilt);
  put(cx - 1, top + 1, cz, BlockId.Gilt);
  put(cx + 1, top + 1, cz, BlockId.Gilt);
  put(cx, top + 2, cz, BlockId.Gilt);
}

/**
 * A white-stone cathedral with domes on it.
 *
 * @param domes 1, 3 or 5 - the three arrangements these churches actually
 * come in, and the reason it is a count rather than a list of positions.
 */
function cathedral(
  put: Put, cx: number, cz: number, half: number, height: number, domes: number,
): void {
  const x0 = cx - half, x1 = cx + half;
  const z0 = cz - half, z1 = cz + half;

  shell(put, x0, x1, 1, height, z0, z1, BlockId.WhiteStone);
  // The roof, and a plank floor under it.
  box(put, x0, x1, height + 1, height + 1, z0, z1, BlockId.WhiteStone);
  box(put, x0 + 1, x1 - 1, 1, 1, z0 + 1, z1 - 1, BlockId.OakPlanks);

  // Windows: a tall slit between every pair of pilasters, on all four walls.
  for (let y = height - 5; y <= height - 3; y++) {
    for (let d = -half + 2; d <= half - 2; d += 3) {
      put(x0, y, cz + d, BlockId.Glass);
      put(x1, y, cz + d, BlockId.Glass);
      put(cx + d, y, z0, BlockId.Glass);
      put(cx + d, y, z1, BlockId.Glass);
    }
  }
  // The doorway, facing the square.
  box(put, cx - 1, cx + 1, 1, 3, z0, z0, BlockId.Air);
  put(cx, 4, cz - half, BlockId.Air);

  // A смолоскип either side of the door, inside.
  put(cx - 2, 2, z0 + 1, BlockId.Torch);
  put(cx + 2, 2, z0 + 1, BlockId.Torch);

  const top = height + 2;
  dome(put, cx, cz, top, true);
  if (domes >= 3) {
    const d = half - 2;
    dome(put, cx - d, cz - d, top, false);
    dome(put, cx + d, cz + d, top, false);
  }
  if (domes >= 5) {
    const d = half - 2;
    dome(put, cx + d, cz - d, top, false);
    dome(put, cx - d, cz + d, top, false);
  }
}

/** A long green-roofed hall: the palace, and the arsenal behind it. */
function hall(
  put: Put, x0: number, x1: number, z0: number, z1: number, height: number,
): void {
  shell(put, x0, x1, 1, height, z0, z1, BlockId.WhiteStone);
  box(put, x0 + 1, x1 - 1, 1, 1, z0 + 1, z1 - 1, BlockId.OakPlanks);
  // Windows in two storeys, which is what makes it a palace and not a shed.
  for (const y of [3, height - 2]) {
    for (let x = x0 + 2; x <= x1 - 2; x += 3) {
      put(x, y, z0, BlockId.Glass);
      put(x, y, z1, BlockId.Glass);
      put(x, y + 1, z0, BlockId.Glass);
      put(x, y + 1, z1, BlockId.Glass);
    }
  }
  // The doorway, in the middle of the long front wall. Without it the palace
  // was a sealed box with windows - which the walk test caught and no amount
  // of looking at the roof would have.
  const doorX = Math.floor((x0 + x1) / 2);
  box(put, doorX - 1, doorX + 1, 1, 3, z0, z0, BlockId.Air);

  // A hipped tile roof, stepping in a course at a time.
  let y = height + 1;
  let inset = 0;
  while (x0 + inset <= x1 - inset && z0 + inset <= z1 - inset) {
    box(put, x0 + inset, x1 - inset, y, y, z0 + inset, z1 - inset, BlockId.GreenTile);
    inset++;
    y++;
  }
}

/**
 * The whole thing.
 *
 * Laid out on paper first, because at this size two buildings that overlap by
 * a block produce a mess nobody can find the cause of afterwards. The one
 * check that catches it is `strayCells`, and a test that counts what each
 * quarter of the site is paved with.
 */
function planKremlin(put: Put, facing: DoorFacing): void {
  void facing;
  const last = KREMLIN_SPAN - 1;

  // The ground: paved everywhere, with grass where the gardens are.
  box(put, 0, last, 0, 0, 0, last, BlockId.StoneTile);
  box(put, 6, 26, 0, 0, 30, 52, BlockId.Grass);
  box(put, 44, 54, 0, 0, 44, 54, BlockId.Grass);

  curtain(put);

  // The gate: an arch through the front wall, under the great tower.
  box(put, 28, 32, 1, 4, 0, WALL_THICK - 1, BlockId.Air);
  box(put, 29, 31, 5, 5, 0, WALL_THICK - 1, BlockId.Air);

  /*
   * The towers.
   *
   * Every centre is at least `half + 1` from the edge, because a tower is
   * wider than its shaft: the machicolated brow and the first course of the
   * roof both oversail it by one. Sitting them on the wall line looked right
   * on paper and put three hundred and twenty-five blocks outside the box,
   * where nothing had been checked and nothing would have been cleared.
   */
  tower(put, 30, 5, 4, 26, true);           // Спаська, the gate tower
  tower(put, 4, 4, 3, 17, false);           // the four corners
  tower(put, last - 4, 4, 3, 17, false);
  tower(put, 4, last - 4, 3, 17, false);
  tower(put, last - 4, last - 4, 3, 17, false);
  tower(put, 4, 30, 3, 15, false);          // mid-wall, west and east
  tower(put, last - 4, 30, 3, 15, false);
  tower(put, 30, last - 4, 3, 15, false);   // Тайницька, at the back

  /*
   * The gate, cut again and cut deeper.
   *
   * Twice, because Спаська was laid over the first cut - and *through* the
   * tower, because the first version stopped at the tower's back wall: you
   * walked in under the arch and stood in a brick cupboard. The passage has
   * to reach the courtyard or it is not a gate.
   */
  box(put, 28, 32, 1, 4, 0, WALL_THICK - 1, BlockId.Air);
  box(put, 29, 31, 1, 5, 0, 9, BlockId.Air);

  // Up onto the walkway, one flight either side of the gate.
  steps(put, WALL_THICK, 12, 1);
  steps(put, last - WALL_THICK, 12, 1);

  /*
   * Clear the walkway, last of all.
   *
   * Stated as the thing we actually want - "you can walk the whole circuit" -
   * rather than as an opening cut in each tower. Seven towers straddle the
   * curtain at seven different angles, and cutting each one to fit left gaps
   * at the corners every time. Sweeping the band once cannot leave a gap,
   * and where it passes through a tower it reads as an archway, which is how
   * these wall-walks actually work.
   *
   * The outer column is left alone: that is where the merlons stand, and they
   * are the only thing between the walkway and a nine-block drop.
   */
  for (let z = 0; z <= last; z++) {
    for (let x = 0; x <= last; x++) {
      const inBand = x < WALL_THICK || z < WALL_THICK ||
        x > last - WALL_THICK || z > last - WALL_THICK;
      if (!inBand) continue;
      if (x === 0 || z === 0 || x === last || z === last) continue;
      box(put, x, x, WALL_TOP + 1, WALL_TOP + 2, z, z, BlockId.Air);
    }
  }
  // The смолоскипи stood in the band and have just been swept away with it.
  for (let along = 6; along < last - 5; along += 9) {
    const inner = WALL_THICK - 1;
    put(inner, WALL_TOP + 1, along, BlockId.Torch);
    put(last - inner, WALL_TOP + 1, along, BlockId.Torch);
    put(along, WALL_TOP + 1, inner, BlockId.Torch);
    put(along, WALL_TOP + 1, last - inner, BlockId.Torch);
  }

  // Грановита палата, on the left of the square.
  hall(put, 8, 24, 10, 24, 9);
  // The arsenal along the back, stopping clear of Тайницька's brow.
  hall(put, 10, 24, 46, 53, 7);

  // Успенський собор: the big one, five domes, facing the square.
  cathedral(put, 36, 20, 6, 13, 5);
  // Архангельський собор, clear of the east mid-wall tower's brow at x 52.
  cathedral(put, 46, 36, 5, 11, 5);
  // Благовіщенський собор, the small one.
  cathedral(put, 34, 40, 4, 10, 3);

  // Дзвіниця Івана Великого: a white bell tower with a gilt dome.
  shell(put, 44, 48, 1, 24, 12, 16, BlockId.WhiteStone);
  for (let y = 8; y <= 22; y += 7) {
    // The belfry openings, one storey above another.
    box(put, 44, 44, y, y + 1, 13, 15, BlockId.Air);
    box(put, 48, 48, y, y + 1, 13, 15, BlockId.Air);
    box(put, 45, 47, y, y + 1, 12, 12, BlockId.Air);
    box(put, 45, 47, y, y + 1, 16, 16, BlockId.Air);
  }
  dome(put, 46, 14, 25, true);

  /*
   * A lair in every building.
   *
   * They used to be scattered over the far island and hunted one at a time.
   * Now the fortress is the nest: each tower, each cathedral, each hall has
   * one standing on its floor, and clearing the кремль room by room is the
   * campaign. A world with no кремль in it has no raids in it.
   *
   * Written last, over whatever the building put there, because a lair inside
   * a wall is unreachable and a lair is only worth having if it can be broken.
   */
  for (const [x, z] of LAIRS) put(x, 1, z, BlockId.OrcSpawner);
}

/**
 * Where the lairs stand: one to a building, on its floor.
 *
 * Written out rather than derived from the buildings, because "the middle of
 * a building" is not a thing this file knows - a tower is a call to `tower`
 * and a cathedral is a call to `cathedral`, and neither hands back where its
 * floor is. A list of fourteen pairs is the honest way to say it, and a test
 * checks every one of them is somewhere a player can actually reach.
 */
const LAIRS: readonly (readonly [number, number])[] = [
  [30, 5],            // Спаська, the gate tower
  [4, 4], [56, 4], [4, 56], [56, 56],   // the four corners
  [4, 30], [56, 30],  // mid-wall, west and east
  [30, 56],           // Тайницька, at the back
  [16, 17],           // Грановита палата
  [17, 49],           // the arsenal
  [36, 24],           // Успенський собор
  // Pushed into the собор's far corner: at its middle it stood eleven and a
  // half blocks from the east tower's lair, and two within one swing of each
  // other are one lair with two blocks in it.
  [48, 40],           // Архангельський собор
  [34, 40],           // Благовіщенський собор
  [46, 14],           // Дзвіниця Івана Великого
];

/** How many lairs a кремль brings with it. */
export const KREMLIN_LAIRS = LAIRS.length;

export const KREMLIN: PlannedBuilding = {
  name: "kremlin",
  label: "кремль",
  anchor: { x: 30, z: 0 },
  size: { x: KREMLIN_SPAN, y: KREMLIN_HEIGHT, z: KREMLIN_SPAN },
  /*
   * It goes down anywhere, and whatever is in the way goes.
   *
   * "ground" - level the earth, refuse on anything built - sounded right and
   * played badly: one orc lair or one placed block anywhere in a hundred and
   * forty thousand cells refused the whole thing, and nothing told you which
   * cell. Now nothing refuses it but the edge of the map, and the game says
   * afterwards how much of your own work it took.
   */
  clears: "everything",
  // Except a lair. Ending the raids has to be done with a sabre.
  spares: [BlockId.OrcSpawner],
  foundation: BlockId.Stone,
  plan: planKremlin,
};
